import React, { useCallback, useState, useEffect } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { Typography, Image } from 'lib/components';
import { Checkbox } from 'lib/components/FormFields';
import VendorRow from './VendorRow';
import { useWindowResize } from 'hooks/useWindowResize';
import { Vendor, AllCheckedStatus } from 'types';
import classes from './index.module.scss';
import { HeaderItem, Sort, SortField } from './types';

import { vendorEmailCompareFn, vendorNameCompareFn } from './sort';

interface Props {
	vendors: Vendor[];
	allChecked: boolean;
	allCheckedStatus: AllCheckedStatus;
	handleAllCheckAction: (e: React.ChangeEvent<HTMLInputElement>) => void;
	checkAction: (id: number, isChecked: boolean) => void;
}

const HEADER_ITEMS: HeaderItem[] = [
	{ label: 'Vendor Name', field: 'name', sort: 'asc' },
	{ label: 'Email', field: 'email', sort: 'asc' },
	{ label: 'Phone' },
];

const HospitalVendorsDataTable = ({
	vendors,
	allChecked,
	allCheckedStatus,
	handleAllCheckAction,
	checkAction,
}: Props) => {
	const { isSmallerDevice } = useWindowResize();
	const [sortFields] = useState(
		HEADER_ITEMS.filter((item) => item.field && item.sort).reduce<Record<string, Sort>>((acc, cur) => {
			if (cur.field && cur.sort) {
				acc[cur.field] = cur!.sort;
			}
			return acc;
		}, {})
	);
	const [currentSortField, setCurrentSortField] = useState<SortField>({
		sort: 'asc',
		field: 'name',
	});
	const [sortedVendors, setSortedVendors] = useState<Vendor[]>(vendors);

	const sortVendors = useCallback(
		(vendors: Vendor[]) => {
			const sorted = [...vendors].sort((a, b) => {
				switch (currentSortField?.sort) {
					case 'asc':
						if (currentSortField?.field === 'name') {
							return vendorNameCompareFn(a, b, 'asc');
						} else if (currentSortField?.field === 'email') {
							return vendorEmailCompareFn(a, b, 'asc');
						}

						return 0;

					case 'desc':
						if (currentSortField?.field === 'name') {
							return vendorNameCompareFn(a, b, 'desc');
						} else if (currentSortField?.field === 'email') {
							return vendorEmailCompareFn(a, b, 'desc');
						}
						return 0;

					default:
						return 0;
				}
			});
			return sorted;
		},
		[currentSortField]
	);

	useEffect(() => {
		setSortedVendors(sortVendors(vendors));
	}, [vendors, sortVendors]);

	useEffect(() => {
		const sorted = sortVendors(sortedVendors);

		setSortedVendors(sorted);
	}, [sortVendors, setSortedVendors]);

	const handleHeaderItemClick = useCallback(
		(headerItem: HeaderItem) => {
			if (headerItem.field && sortFields[headerItem.field]) {
				if (headerItem.field === currentSortField?.field) {
					setCurrentSortField((prev) => {
						return {
							...prev,
							sort: prev?.sort === 'asc' ? 'desc' : 'asc',
						};
					});
				} else if (currentSortField?.field !== headerItem.field) {
					setCurrentSortField({ field: headerItem.field, sort: 'desc' });
				}
			}
		},
		[currentSortField, sortFields, setCurrentSortField]
	);

	const renderHeaderItem = (headerItem: HeaderItem) => {
		if (!headerItem.field || !headerItem.sort) {
			return (
				<div key={Math.random()} className={classes['col']}>
					<Typography weight="light" variant={isSmallerDevice ? 'h5' : 'h6'}>
						{headerItem.label}
					</Typography>
					{/* <Image src={'icTableDown'} /> */}
				</div>
			);
		} else {
			return (
				<div
					onClick={() => handleHeaderItemClick(headerItem)}
					key={Math.random()}
					className={classes['col']}
					style={{
						cursor: 'pointer',
					}}
				>
					<Typography weight="light" variant={isSmallerDevice ? 'h5' : 'h6'}>
						{headerItem.label}
					</Typography>
					<Image
						src={'icTableDown'}
						style={{
							marginLeft: '0.5rem',
							transform:
								currentSortField?.field === headerItem.field && currentSortField.sort === 'desc'
									? 'rotate(180deg)'
									: 'initial',
						}}
					/>
				</div>
			);
		}
	};

	return (
		<div className={classes['inventories-datatable']}>
			<div className={classes['table-wrapper']}>
				<div className={classes['table-container']}>
					<div className={classes['toolbar']}>
						<Checkbox
							checked={allChecked}
							onChange={handleAllCheckAction}
							style={{
								margin: '0 2rem 0.1rem 2rem',
							}}
						/>
						{HEADER_ITEMS.map((item) => renderHeaderItem(item))}
					</div>
					<div className={classes['container']}>
						<Scrollbars
							style={{
								flex: 1,
							}}
						>
							{sortedVendors.map((item) => (
								<VendorRow
									key={item.id}
									vendor={item}
									checkAction={checkAction}
									allCheckedStatus={allCheckedStatus}
								/>
							))}
						</Scrollbars>
					</div>
				</div>
			</div>
		</div>
	);
};

export default HospitalVendorsDataTable;
